import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "../store";
import { X, CreditCard, QrCode, Coins } from "lucide-react";

export default function Cart() {
  const { cart, removeFromCart, t, locale, formatPrice } = useApp();
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const subtotal = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);

  const checkout = async () => {
    if (cart.length === 0) return;
    setLoading(true); setErr("");
    const items = cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity, options: c.options }));
    const payload = {
      items,
      customer_email: customer.email || null,
      customer_name: customer.name || null,
      customer_phone: customer.phone || null,
      origin_url: window.location.origin,
      locale,
    };
    try {
      if (method === "card") {
        const res = await axios.post(`${API}/checkout/session`, payload);
        window.location.href = res.data.url;
      } else if (method === "pix") {
        const res = await axios.post(`${API}/payments/pix/create`, payload);
        if (res.data.mocked) {
          window.location.href = `/checkout/pix-success?order_id=${res.data.order_id}&mocked=1&amount=${res.data.amount_brl}`;
        } else {
          window.location.href = res.data.url;
        }
      } else if (method === "usdt") {
        const res = await axios.post(`${API}/payments/usdt/create`, payload);
        window.location.href = `/checkout/usdt/${res.data.order_id}`;
      }
    } catch (e) {
      setErr("Checkout error: " + (e.response?.data?.detail || e.message));
      setLoading(false);
    }
  };

  return (
    <main className="pt-32 pb-32 bg-white min-h-screen" data-testid="cart-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="overline mb-4">Checkout</div>
        <h1 className="font-serif-display text-5xl md:text-7xl text-slate-900">{t.cart.title}</h1>

        {cart.length === 0 ? (
          <div className="mt-20 text-center text-slate-500" data-testid="cart-empty">
            <p>{t.cart.empty}</p>
            <Link to="/products/efoils" className="btn-primary mt-8 inline-flex">{t.cart.shopNow}</Link>
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
              {cart.map((it) => (
                <div key={it.key} className="flex gap-5 bg-slate-50 border border-slate-200 rounded-md p-4 items-center" data-testid={`cart-item-${it.slug}`}>
                  <img src={it.image} alt={it.name} className="w-28 h-28 object-cover rounded" />
                  <div className="flex-1">
                    <div className="font-serif-display text-2xl text-slate-900">{it.name}</div>
                    <div className="text-slate-500 text-xs mt-1">
                      {Object.entries(it.options || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </div>
                    <div className="text-sky-600 font-ui mt-2 font-semibold">{formatPrice(it.unit_price)} × {it.quantity}</div>
                  </div>
                  <button onClick={() => removeFromCart(it.key)} className="text-slate-400 hover:text-slate-700 p-2" data-testid="cart-remove"><X size={18} /></button>
                </div>
              ))}
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-md p-8 h-fit space-y-5 font-ui">
              <div>
                <div className="overline">{t.cart.subtotal}</div>
                <div className="font-serif-display text-5xl text-sky-600 mt-1" data-testid="cart-subtotal">{formatPrice(subtotal)}</div>
              </div>

              <div className="space-y-2">
                <input placeholder={t.cart.name} value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="w-full bg-white border border-slate-300 px-4 py-3 rounded-md focus:border-sky-500 focus:outline-none" data-testid="customer-name" />
                <input type="email" placeholder={t.cart.email} value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="w-full bg-white border border-slate-300 px-4 py-3 rounded-md focus:border-sky-500 focus:outline-none" data-testid="customer-email" />
                <input type="tel" placeholder={t.cart.phone} value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="w-full bg-white border border-slate-300 px-4 py-3 rounded-md focus:border-sky-500 focus:outline-none" data-testid="customer-phone" />
              </div>

              <div>
                <div className="overline mb-2">{t.cart.payWith}</div>
                <div className="space-y-2">
                  <PayOption sel={method === "card"} onClick={() => setMethod("card")} icon={<CreditCard size={18} />} label={t.cart.payCard} sub="Stripe · Cards · Apple/Google Pay" testid="pay-card" />
                  <PayOption sel={method === "pix"} onClick={() => setMethod("pix")} icon={<QrCode size={18} />} label={t.cart.payPix} sub="InfinitePay · Brazil" testid="pay-pix" />
                  <PayOption sel={method === "usdt"} onClick={() => setMethod("usdt")} icon={<Coins size={18} />} label={t.cart.payCrypto} sub="USDT TRC20 · TRON network" testid="pay-usdt" />
                </div>
              </div>

              <button onClick={checkout} disabled={loading} className="btn-primary w-full !justify-center !py-5" data-testid="checkout-btn">
                {loading ? "Redirecting…" : t.cart.checkout}
              </button>
              {err && <div className="text-red-500 text-xs">{err}</div>}
              <div className="text-[10px] text-slate-400 tracking-widest uppercase">Secure payment · SSL encrypted</div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function PayOption({ sel, onClick, icon, label, sub, testid }) {
  return (
    <button onClick={onClick} type="button" data-testid={testid}
      className={`w-full text-left p-3 rounded-md border-2 flex items-center gap-3 transition-all ${sel ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-400"}`}>
      <div className={`w-9 h-9 rounded-full grid place-items-center ${sel ? "bg-sky-500 text-white" : "bg-slate-200 text-slate-600"}`}>{icon}</div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{sub}</div>
      </div>
      <div className={`w-4 h-4 rounded-full border-2 ${sel ? "border-sky-500 bg-sky-500" : "border-slate-300"}`}></div>
    </button>
  );
}
